import { createContext, useCallback, useEffect, useMemo, useState } from 'react';

const STORAGE_KEY = 'recica.theme';
const CHOICES = ['light', 'dark', 'system'];

export const ThemeContext = createContext(null);

function readChoice() {
  if (typeof window === 'undefined') {
    return 'system';
  }
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (CHOICES.includes(stored)) {
      return stored;
    }
  } catch {
    // localStorage may throw in private mode - fall through to default
  }
  return 'system';
}

function systemPrefersLight() {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-color-scheme: light)').matches
  );
}

function resolveActiveTheme(choice) {
  if (choice === 'light' || choice === 'dark') {
    return choice;
  }
  return systemPrefersLight() ? 'light' : 'dark';
}

export function ThemeProvider({ children }) {
  const [themeChoice, setThemeChoiceState] = useState(() => readChoice());
  const [activeTheme, setActiveTheme] = useState(() => resolveActiveTheme(readChoice()));

  // Reflect the active theme on <html data-theme="…"> so CSS variables flip.
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', activeTheme);

    /* Und die Statusleiste mitziehen.
     *
     * Als App vom Startbildschirm gibt es keine Adresszeile mehr - die
     * Statusleiste des Geräts grenzt direkt an die Kopfzeile der Anwendung.
     * Trägt sie die falsche Farbe, sieht man oben einen Streifen, der nicht
     * dazugehört, und genau daran erkennt man eine Webseite im App-Kleid.
     *
     * Die beiden <meta>-Angaben im Dokument folgen nur der Einstellung des
     * BETRIEBSSYSTEMS. Wer in der Anwendung ausdrücklich hell oder dunkel
     * wählt, während das Gerät das Gegenteil sagt, bekäme sonst weiterhin die
     * Leiste der Systemeinstellung. */
    const farbe = activeTheme === 'dark' ? '#070d19' : '#ffffff';
    for (const tag of document.querySelectorAll('meta[name="theme-color"]')) {
      /* Die mediengebundenen Angaben stehen weiter im Dokument - sie greifen
         beim allerersten Laden, bevor React etwas gezeichnet hat. Danach
         gewinnt die ausdrückliche Wahl. */
      tag.setAttribute('content', farbe);
    }
  }, [activeTheme]);

  // When the choice changes, recompute the active theme.
  useEffect(() => {
    setActiveTheme(resolveActiveTheme(themeChoice));
  }, [themeChoice]);

  // Only when the user picked 'system' do we follow OS-level changes.
  useEffect(() => {
    if (themeChoice !== 'system' || typeof window.matchMedia !== 'function') {
      return undefined;
    }
    const mq = window.matchMedia('(prefers-color-scheme: light)');
    const handler = (event) => setActiveTheme(event.matches ? 'light' : 'dark');
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [themeChoice]);

  // Keep tabs in sync if the user changes the choice elsewhere.
  useEffect(() => {
    function onStorage(event) {
      if (event.key === STORAGE_KEY && CHOICES.includes(event.newValue)) {
        setThemeChoiceState(event.newValue);
      }
    }
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const setThemeChoice = useCallback((next) => {
    if (!CHOICES.includes(next)) {
      return;
    }
    setThemeChoiceState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Ignore - state updated, only persistence is missing.
    }
  }, []);

  const value = useMemo(
    () => ({
      themeChoice,
      activeTheme,
      isDark: activeTheme === 'dark',
      setThemeChoice,
    }),
    [themeChoice, activeTheme, setThemeChoice],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}
